import { graphql } from "gatsby"
import uniqid from "uniqid"
import { filterByLocale } from "@helpers/miscHelpers"

const menuTreeFromPages = (nodes, content, megaMenus) => {
  const allMenuItems = nodes
    .filter(node => node.pageContext !== null && node.pageContext.showPage)
    .map(node => ({ path: node.path, ...node.pageContext }))

  const localisedMegaMenus = filterByLocale(megaMenus)

  const datoLandingPages = localisedMegaMenus
    .sort((a, b) => a.position - b.position)
    .map(megaMenu => {
      const {
        id,
        locales,
        category,
        position,
        title,
        columns,
        setToEnd
      } = megaMenu
      return {
        hasParent: false,
        id,
        language: locales,
        path: category.path,
        position: position,
        showPage: true,
        slug: category.slug,
        title,
        columns,
        treeChildren: [],
        treeParent: null,
        isMegaMenu: columns?.length > 0,
        setToEnd: setToEnd
      }
    })

  // Find the learn mega menu from DatoCMS
  const learnMegaMenu = datoLandingPages.find(menu => menu.path === "/learn/")

  if (learnMegaMenu) {
    learnMegaMenu.position = 2 // Set position after products
    learnMegaMenu.isMegaMenu = true // Ensure it's treated as a mega menu
    learnMegaMenu.isLearnMenu = true // Set the isLearnMenu flag
    learnMegaMenu.title = "Learn Centre" // Set the title to Learn Centre
    // Keep the original columns for desktop mega menu
    const originalLearnMenu = localisedMegaMenus.find(
      menu => menu.category.path === "/learn/"
    )

    if (originalLearnMenu) {
      // Process columns to handle external links
      learnMegaMenu.columns = originalLearnMenu.columns.map(column => {
        if (column.collections) {
          column.collections = column.collections.map(item => {
            // If the item has a path that starts with http, it's an external link
            if (item.path && item.path.startsWith("http")) {
              return {
                ...item,
                isExternal: true
              }
            }
            return item
          })
        }
        return column
      })
    }
  }

  // Filter out the Learn menu from datoLandingPages to avoid duplication
  const filteredDatoLandingPages = datoLandingPages.filter(
    menu => menu.path !== "/learn/"
  )

  const swimSpas = allMenuItems
    .filter(item => item.path === "/swim-spas/")
    .map(item => {
      return {
        ...item,
        title: "Swim Spas"
      }
    })

  const datoPages = allMenuItems
    .filter(
      item =>
        item.hasParent === false &&
        item.type === "DatoCmsPage" &&
        item.path !== "/learn/"
    )
    .sort((a, b) => a.position - b.position)

  datoPages.forEach(parent => {
    const { treeChildren, path } = parent

    if (treeChildren.length > 0 && path === "/contact-us/") {
      parent.title = "Contact"
      parent.treeChildren = treeChildren
        .filter(child => child.showPage)
        .map(child => ({
          ...child,
          path: `${path}${child.slug}/`
        }))
        .sort((a, b) => a.position - b.position)

      process.env.GATSBY_LANGUAGE === "en-AU" &&
        parent.treeChildren.unshift({
          id: uniqid(),
          path: "/services/",
          showPage: true,
          slug: "services",
          title: "Services",
          language: process.env.GATSBY_LANGUAGE,
          position: 3
        })

      parent.treeChildren.unshift(
        {
          id: uniqid(),
          path: "/contact-us/",
          showPage: true,
          slug: "contact-us",
          title: "Contact Us",
          language: process.env.GATSBY_LANGUAGE,
          position: 0
        },
        {
          id: uniqid(),
          path: "/spa-parts/",
          showPage: true,
          slug: "spa-parts",
          title: "Parts & Chemicals",
          language: process.env.GATSBY_LANGUAGE,
          position: 1,
          isExternal: true
        },
        {
          id: uniqid(),
          path: content.customerSupport,
          showPage: true,
          title: "Customer Support",
          language: process.env.GATSBY_LANGUAGE,
          position: 2
        }
      )
    } else if (treeChildren.length > 0) {
      parent.treeChildren = treeChildren
        .filter(child => child.showPage)
        .map(child => ({
          ...child,
          path: `${path}${child.slug}/`
        }))
        .sort((a, b) => a.position - b.position)
    }
  })

  const customMenuItems = allMenuItems
    .filter(
      item =>
        item.path === "/specials/combined/" || item.path === "/find-a-showroom/"
    )
    .sort((a, b) => a.position - b.position)
    .map(item => {
      return {
        ...item,
        title: item.title === "Find a Showroom" ? "Showrooms" : item.title
      }
    })

  // Add Parts and Accessories menu item for en-AU locale
  if (process.env.GATSBY_LANGUAGE === "en-AU") {
    customMenuItems.unshift({
      id: uniqid(),
      path: "https://shop.spaworld.com.au/",
      showPage: true,
      title: "Parts and Accessories",
      language: "en-AU",
      position: 0,
      isExternal: true,
      isMobileOnly: true
    })
  }

  const spaPools = allMenuItems
    .filter(item => item.path === "/spa-pools/")
    .map(item => {
      return {
        ...item,
        title: "Spa Pools"
      }
    })

  const saunas = allMenuItems
    .filter(item => item.path === "/saunas/")
    .map(item => {
      return {
        ...item,
        title: "Saunas"
      }
    })

  customMenuItems.forEach(parent => {
    const { treeChildren, path } = parent

    if (treeChildren?.length > 0) {
      parent.treeChildren = treeChildren
        .filter(child => child.showPage)
        .map(child => ({
          ...child,
          path: child.slug === "specials" ? path : `${path}${child.slug}/`
        }))
        .sort((a, b) => a.position - b.position)
    }
  })

  const endLandingPages = []
  datoLandingPages.forEach(page => {
    if (page.setToEnd) {
      endLandingPages.push(page)
    }
  })

  if (
    !endLandingPages[0]?.columns[0].collections.find(
      link => link.title === "FAQs"
    )
  ) {
    endLandingPages[0]?.columns[0].collections.push({
      id: uniqid(),
      path: content.faq,
      showPage: true,
      title: "FAQs",
      position: 3
    })
  }

  return [
    ...spaPools,
    ...swimSpas,
    ...saunas,
    ...filteredDatoLandingPages.filter(p => !p.setToEnd),
    ...customMenuItems,
    ...endLandingPages,
    learnMegaMenu,
    ...datoPages
  ]
}

export { menuTreeFromPages }

export const query = graphql`
  fragment MenuHelpersAllPages on SitePage {
    path
    pageContext
  }
`
